import { findSeamConnection } from '../helpers';
import { nearestSixteenth } from '../../../helpers';
import { FLAT_TRIM_HALF_WIDTH, FLAT_TRIM_WIDTH } from '../../surround-material/constants';

import type { PanelTrim } from '../panel-trim.class';
import type { FlatTrimAlignment, FlatTrimDimensions } from '../types';

export const getFlatTrimDimensionsCfn = (panelTrim: PanelTrim): FlatTrimDimensions => {
	const { panels, refMap: { island }} = panelTrim;
	const [firstId, secondId] = panels;

	const first = island?.panels.findById(firstId) ?? null;
	const second = island?.panels.findById(secondId) ?? null;

	// same expression get-rendering-details.cfn.ts uses for the trim depth
	const nearHeight = Math.max(first?.size.height ?? 0, second?.size.height ?? 0);
	const dimensions: FlatTrimDimensions = {
		width: FLAT_TRIM_WIDTH,
		halfWidth: FLAT_TRIM_HALF_WIDTH,
		nearHeight,
		farHeight: nearHeight,
		isStepped: false,
		alignment: 'none'
	};

	if (!first || !second || !island) return dimensions;

	// same seam resolution get-paired-seam-trim.cfn.ts uses for pairing - deliberately NOT gated on
	// connectedPanel.isSidePanel. An L-shape routinely joins one island's front/back panel to the other's
	// side panel, so a side-panel gate makes this resolve on only ONE of the two paired trims, and the two
	// halves of the same physical piece must agree on nearHeight/farHeight/isStepped
	const seam = findSeamConnection([first, second], island);
	if (!seam) return dimensions;

	const { connectedPanel, connection } = seam;

	const farHeight = connection.targetPanel?.size.height ?? connection.targetIsland?.size.height ?? nearHeight;
	const isStepped = nearestSixteenth(nearHeight) !== nearestSixteenth(farHeight);
	if (!isStepped) return { ...dimensions, farHeight };

	// the strip lies in the plane of the panel that does NOT carry the island connection
	const facePanel = connectedPanel.id === first.id ? second : first;
	const otherPanel = connectedPanel.id === first.id ? first : second;

	// same convention as PanelTrim.center - the shared corner is facePanel's left end when otherPanel is its left neighbour
	const seamAtFaceLeftEdge = facePanel.connections.left?.targetPanelId === otherPanel.id;
	const nearSide: FlatTrimAlignment = seamAtFaceLeftEdge ? 'right' : 'left';
	const farSide: FlatTrimAlignment = seamAtFaceLeftEdge ? 'left' : 'right';

	return {
		...dimensions,
		farHeight,
		isStepped,
		alignment: nearHeight > farHeight ? nearSide : farSide
	};
};
